import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Form, Row, Col, Modal, Button, Spinner } from 'react-bootstrap';
import TextInput from '../../form-generator/components/TextInput';
import NumberInput from '../../form-generator/components/NumberInput';
import DropDownInput from '../../form-generator/components/DropDownInput';
import TagsInput from '../../form-generator/components/TagsInput';
import { BUSSINESS_SOURCE_OPTIONS, SALES_PERSON_ROLE } from '../../helpers/constants';

const OrganizationFormModal = ({ show, organization, users = [], onHide, onSubmit, showProgress }) => {
  const isEdit = !!organization;

  // Compute initial form data from organization prop
  const initialFormData = useMemo(
    () => ({
      name: organization?.name || '',
      lienSearchProcessingFee: organization?.lienSearchProcessingFee ?? 120,
      estoppelProcessingFee: organization?.estoppelProcessingFee ?? 100,
      surveyProcessingFee: organization?.surveyProcessingFee ?? 95,
      lienSearchMinimumInvoicableAmount: organization?.lienSearchMinimumInvoicableAmount ?? 275,
      estoppelMinimumInvoicableAmount: organization?.estoppelMinimumInvoicableAmount ?? 275,
      surveyMinimumInvoicableAmount: organization?.surveyMinimumInvoicableAmount ?? 275,
      businessSource: organization?.businessSource || '',
      accountManager: organization?.accountManager?._id || organization?.accountManager || null,
      leadGenerator: organization?.leadGenerator?._id || organization?.leadGenerator || null,
      salesPerson: organization?.salesPerson?._id || organization?.salesPerson || null,
      emails: organization?.emails || [],
      isEnabled: organization?.isEnabled !== undefined ? (organization?.isEnabled ? 'Enable' : 'Disable') : 'Enable',
      searchCondominiumInAutomations:
        organization?.searchCondominiumInAutomations !== undefined
          ? organization?.searchCondominiumInAutomations
            ? 'Yes'
            : 'No'
          : 'Yes',
      newAdmin: {
        name: '',
        email: '',
        phone: ''
      }
    }),
    [organization]
  );

  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});

  // Reset form data when modal opens or organization changes
  useEffect(() => {
    if (show) {
      setFormData(initialFormData);
      setErrors({});
    } else {
      // Clear form data when modal closes
      setFormData({
        name: '',
        lienSearchProcessingFee: null,
        estoppelProcessingFee: null,
        surveyProcessingFee: null,
        lienSearchMinimumInvoicableAmount: null,
        estoppelMinimumInvoicableAmount: null,
        surveyMinimumInvoicableAmount: null,
        businessSource: '',
        accountManager: null,
        leadGenerator: null,
        salesPerson: null,
        emails: [],
        isEnabled: 'Enable',
        searchCondominiumInAutomations: 'Yes',
        newAdmin: {
          name: '',
          email: '',
          phone: ''
        }
      });
      setErrors({});
    }
  }, [show, initialFormData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleNestedInputChange = (parentField, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parentField]: {
        ...prev[parentField],
        [field]: value
      }
    }));
    // Clear error for nested field when user starts typing
    const errorKey = `${parentField}.${field}`;
    if (errors[errorKey]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  const handleEmailsChange = useCallback(e => {
    try {
      const tags = JSON.parse(e.target.value || '[]');
      setFormData(prev => ({
        ...prev,
        emails: tags
      }));
    } catch (err) {
      // Ignore parse errors
    }
  }, []);

  const validateForm = () => {
    const newErrors = {};

    // Validate Name (always required)
    if (!formData.name || formData.name.trim() === '') {
      newErrors.name = 'Name is required';
    }

    // Validate Admin Name (required when not editing)
    if (!isEdit) {
      if (!formData.newAdmin.name || formData.newAdmin.name.trim() === '') {
        newErrors['newAdmin.name'] = 'Admin Name is required';
      }

      // Validate Admin Email (required when not editing)
      if (!formData.newAdmin.email || formData.newAdmin.email.trim() === '') {
        newErrors['newAdmin.email'] = 'Admin Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.newAdmin.email.trim())) {
        newErrors['newAdmin.email'] = 'Please enter a valid email address';
      }
    }

    if (formData.lienSearchMinimumInvoicableAmount === null || formData.lienSearchMinimumInvoicableAmount === '') {
      newErrors.lienSearchMinimumInvoicableAmount = 'Lien Search minimum invoicable amount is required';
    }
    if (formData.estoppelMinimumInvoicableAmount === null || formData.estoppelMinimumInvoicableAmount === '') {
      newErrors.estoppelMinimumInvoicableAmount = 'Estoppel minimum invoicable amount is required';
    }
    if (formData.surveyMinimumInvoicableAmount === null || formData.surveyMinimumInvoicableAmount === '') {
      newErrors.surveyMinimumInvoicableAmount = 'Survey minimum invoicable amount is required';
    }

    // can't be less than 0
    if (formData.lienSearchMinimumInvoicableAmount < 0) {
      newErrors.lienSearchMinimumInvoicableAmount = 'Lien Search minimum invoicable amount cannot be less than 0';
    }
    if (formData.estoppelMinimumInvoicableAmount < 0) {
      newErrors.estoppelMinimumInvoicableAmount = 'Estoppel minimum invoicable amount cannot be less than 0';
    }
    if (formData.surveyMinimumInvoicableAmount < 0) {
      newErrors.surveyMinimumInvoicableAmount = 'Survey minimum invoicable amount cannot be less than 0';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = e => {
    const normalize = value => (value === 'dropdown-placeholder' || value === '' ? null : value);

    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const submitData = {
      ...formData,
      businessSource: normalize(formData.businessSource),
      accountManager: normalize(formData.accountManager),
      leadGenerator: normalize(formData.leadGenerator),
      salesPerson: normalize(formData.salesPerson),
      isEnabled: formData.isEnabled === 'Enable',
      searchCondominiumInAutomations: formData.searchCondominiumInAutomations === 'Yes'
    };

    // For add form, include newAdmin fields in the format expected by the API
    if (!isEdit && (formData.newAdmin.name || formData.newAdmin.email)) {
      submitData['newAdmin.name'] = formData.newAdmin.name;
      submitData['newAdmin.email'] = formData.newAdmin.email;
      submitData['newAdmin.phone'] = formData.newAdmin.phone;
    }

    if (onSubmit) {
      onSubmit(submitData);
    }
  };

  const userOptions = users.map(u => u.name);
  const userOptionValues = users.map(u => u._id);

  const salesPersons = users.filter(u => u.role === SALES_PERSON_ROLE);
  const salesPersonOptions = salesPersons.map(u => u.name);
  const salesPersonOptionValues = salesPersons.map(u => u._id);

  return (
    <Modal size="lg" show={show} onHide={onHide} centered backdrop="static">
      <Modal.Header closeButton={!showProgress}>
        <Modal.Title>
          <h6 className="mb-0 xlargeFont font-weight-bold">
            {organization ? `Update Organization` : `Invite Organization`}
          </h6>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="overflow-auto py-3 px-4">
        <Form id="organization-form" onSubmit={handleSubmit}>
          {/* Section 1: Basic Information */}
          <div className="mb-3">
            <h6 className="mb-2 font-weight-bold text-secondary">Basic Information</h6>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label>
                    Name <span className="text-danger">*</span>
                  </Form.Label>
                  <TextInput
                    id="name"
                    title="Name"
                    required
                    preValue={formData.name}
                    className={errors.name ? 'is-invalid' : ''}
                    onFormChange={e => handleInputChange('name', e.target.value)}
                  />
                  {errors.name && <div className="text-danger small mt-1">{errors.name}</div>}
                </Form.Group>
              </Col>
              {!isEdit && (
                <Col md={6}>
                  <Form.Group className="mb-2">
                    <Form.Label>
                      Admin Name <span className="text-danger">*</span>
                    </Form.Label>
                    <TextInput
                      id="newAdmin.name"
                      title="Admin Name"
                      required
                      preValue={formData.newAdmin.name}
                      className={errors['newAdmin.name'] ? 'is-invalid' : ''}
                      onFormChange={e => handleNestedInputChange('newAdmin', 'name', e.target.value)}
                    />
                    {errors['newAdmin.name'] && <div className="text-danger small mt-1">{errors['newAdmin.name']}</div>}
                  </Form.Group>
                </Col>
              )}
            </Row>
            {!isEdit && (
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-2">
                    <Form.Label>
                      Admin Email <span className="text-danger">*</span>
                    </Form.Label>
                    <TextInput
                      id="newAdmin.email"
                      title="Admin Email"
                      variant="email"
                      required
                      preValue={formData.newAdmin.email}
                      className={errors['newAdmin.email'] ? 'is-invalid' : ''}
                      onFormChange={e => handleNestedInputChange('newAdmin', 'email', e.target.value)}
                    />
                    {errors['newAdmin.email'] && (
                      <div className="text-danger small mt-1">{errors['newAdmin.email']}</div>
                    )}
                    <small className="text-muted d-block mt-1">
                      The invitation link will be sent to this email address.
                    </small>
                  </Form.Group>
                </Col>

                {!isEdit && (
                  <Col md={6}>
                    <Form.Group className="mb-2">
                      <Form.Label>Admin Phone</Form.Label>
                      <TextInput
                        id="newAdmin.phone"
                        title="Admin Phone"
                        variant="tel"
                        preValue={formData.newAdmin.phone}
                        onFormChange={e => handleNestedInputChange('newAdmin', 'phone', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                )}
              </Row>
            )}
          </div>

          {/* Section 2: Processing Fees Details */}
          <div className="mb-3">
            <h6 className="mb-2 font-weight-bold text-secondary">Processing Fees</h6>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-2">
                  <Form.Label>Lien Search</Form.Label>
                  <NumberInput
                    id="lienSearchProcessingFee"
                    title="Lien Search Processing Fee"
                    preValue={formData.lienSearchProcessingFee}
                    onFormChange={e => handleInputChange('lienSearchProcessingFee', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-2">
                  <Form.Label>Estoppel</Form.Label>
                  <NumberInput
                    id="estoppelProcessingFee"
                    title="Estoppel Processing Fee"
                    preValue={formData.estoppelProcessingFee}
                    onFormChange={e => handleInputChange('estoppelProcessingFee', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-2">
                  <Form.Label>Survey</Form.Label>
                  <NumberInput
                    id="surveyProcessingFee"
                    title="Survey Processing Fee"
                    preValue={formData.surveyProcessingFee}
                    onFormChange={e => handleInputChange('surveyProcessingFee', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
          </div>
          {/* Section 3: Minimum Invoicable Amount */}
          <div className="mb-3">
            <h6 className="mb-2 font-weight-bold text-secondary">Minimum Invoicable Amount</h6>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-2">
                  <Form.Label>
                    Lien Search <span className="text-danger">*</span>
                  </Form.Label>
                  <NumberInput
                    key={`lienSearchMinimumInvoicableAmount-${show}-${organization?._id || 'new'}-${
                      initialFormData.lienSearchMinimumInvoicableAmount
                    }`}
                    id="lienSearchMinimumInvoicableAmount"
                    title="Lien Search Minimum Invoicable Amount"
                    required
                    preValue={formData.lienSearchMinimumInvoicableAmount}
                    onFormChange={e => handleInputChange('lienSearchMinimumInvoicableAmount', e.target.value)}
                  />
                  {errors.lienSearchMinimumInvoicableAmount && (
                    <div className="text-danger small mt-1">{errors.lienSearchMinimumInvoicableAmount}</div>
                  )}
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-2">
                  <Form.Label>
                    Estoppel <span className="text-danger">*</span>
                  </Form.Label>
                  <NumberInput
                    key={`estoppelMinimumInvoicableAmount-${show}-${organization?._id || 'new'}-${
                      initialFormData.estoppelMinimumInvoicableAmount
                    }`}
                    id="estoppelMinimumInvoicableAmount"
                    title="Estoppel Minimum Invoicable Amount"
                    required
                    preValue={formData.estoppelMinimumInvoicableAmount}
                    onFormChange={e => handleInputChange('estoppelMinimumInvoicableAmount', e.target.value)}
                  />
                  {errors.estoppelMinimumInvoicableAmount && (
                    <div className="text-danger small mt-1">{errors.estoppelMinimumInvoicableAmount}</div>
                  )}
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-2">
                  <Form.Label>
                    Survey <span className="text-danger">*</span>
                  </Form.Label>
                  <NumberInput
                    key={`surveyMinimumInvoicableAmount-${show}-${organization?._id || 'new'}-${
                      initialFormData.surveyMinimumInvoicableAmount
                    }`}
                    id="surveyMinimumInvoicableAmount"
                    title="Survey Minimum Invoicable Amount"
                    required
                    preValue={formData.surveyMinimumInvoicableAmount}
                    onFormChange={e => handleInputChange('surveyMinimumInvoicableAmount', e.target.value)}
                  />
                  {errors.surveyMinimumInvoicableAmount && (
                    <div className="text-danger small mt-1">{errors.surveyMinimumInvoicableAmount}</div>
                  )}
                </Form.Group>
              </Col>
            </Row>
          </div>

          <div className="mb-3">
            <h6 className="mb-2 font-weight-bold text-secondary">Additional Details</h6>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-2">
                  <Form.Label>Business Source</Form.Label>
                  <DropDownInput
                    key={`businessSource-${show}-${organization?._id || 'new'}-${organization?.businessSource || ''}`}
                    id="businessSource"
                    title="Business Source"
                    options={BUSSINESS_SOURCE_OPTIONS}
                    preValue={initialFormData.businessSource}
                    onFormChange={e => handleInputChange('businessSource', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-2">
                  <Form.Label>Account Manager</Form.Label>
                  <DropDownInput
                    key={`accountManager-${show}-${organization?._id || 'new'}-${organization?.accountManager?._id ||
                      organization?.accountManager ||
                      ''}`}
                    id="accountManager"
                    title="Account Manager"
                    options={userOptions}
                    optionValues={userOptionValues}
                    preValue={initialFormData.accountManager}
                    onFormChange={e => handleInputChange('accountManager', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-2">
                  <Form.Label>Lead Generator</Form.Label>
                  <DropDownInput
                    key={`leadGenerator-${show}-${organization?._id || 'new'}-${organization?.leadGenerator?._id ||
                      organization?.leadGenerator ||
                      ''}`}
                    id="leadGenerator"
                    title="Lead Generator"
                    options={userOptions}
                    optionValues={userOptionValues}
                    preValue={initialFormData.leadGenerator}
                    onFormChange={e => handleInputChange('leadGenerator', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-2">
                  <Form.Label>Sales Person</Form.Label>
                  <DropDownInput
                    key={`salesPerson-${show}-${organization?._id || 'new'}-${organization?.salesPerson?._id ||
                      organization?.salesPerson ||
                      ''}`}
                    id="salesPerson"
                    title="Sales Person"
                    options={salesPersonOptions}
                    optionValues={salesPersonOptionValues}
                    preValue={initialFormData.salesPerson}
                    onFormChange={e => handleInputChange('salesPerson', e.target.value)}
                  />
                </Form.Group>
              </Col>

              {isEdit && (
                <Col md={4}>
                  <Form.Group className="mb-2">
                    <Form.Label>
                      Status <span className="text-danger">*</span>
                    </Form.Label>
                    <DropDownInput
                      key={`isEnabled-${show}-${organization?._id || 'new'}-${
                        organization?.isEnabled !== undefined ? (organization?.isEnabled ? 'Enable' : 'Disable') : ''
                      }`}
                      id="isEnabled"
                      title="Status"
                      options={['Enable', 'Disable']}
                      required
                      preValue={initialFormData.isEnabled}
                      className={errors.isEnabled ? 'is-invalid' : ''}
                      onFormChange={e => handleInputChange('isEnabled', e.target.value)}
                    />
                    {errors.isEnabled && <div className="text-danger small mt-1">{errors.isEnabled}</div>}
                  </Form.Group>
                </Col>
              )}
              <Col md={4}>
                <Form.Group className="mb-2">
                  <Form.Label>
                    Search Condominium
                    <span className="tinyFont"> (In Automations)</span>
                  </Form.Label>
                  <DropDownInput
                    key={`searchCondominiumInAutomations-${show}-${organization?._id || 'new'}-${
                      organization?.searchCondominiumInAutomations !== undefined
                        ? organization?.searchCondominiumInAutomations
                          ? 'Yes'
                          : 'No'
                        : 'Yes'
                    }`}
                    id="searchCondominiumInAutomations"
                    title="Search Condominium in Automations"
                    options={['Yes', 'No']}
                    preValue={initialFormData.searchCondominiumInAutomations}
                    onFormChange={e => handleInputChange('searchCondominiumInAutomations', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col>
                <Form.Group className="mb-2">
                  <Form.Label>Emails</Form.Label>
                  <TagsInput
                    key={`emails-${show}-${organization?._id || 'new'}-${JSON.stringify(organization?.emails || [])}`}
                    id="emails"
                    title="Emails"
                    placeholder="Enter email address..."
                    inputType="email"
                    preValue={initialFormData.emails}
                    onFormChange={handleEmailsChange}
                  />
                </Form.Group>
              </Col>
            </Row>
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={showProgress}>
          Cancel
        </Button>
        <Button
          variant="info"
          className="d-inline-flex align-items-center justify-content-center"
          onClick={handleSubmit}
          disabled={showProgress}
        >
          {showProgress && <Spinner size="sm" animation="border" className="mr-1" />}
          {organization ? 'Update' : 'Invite'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default OrganizationFormModal;
